import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { updatePassword } from "@/lib/auth";
import {
  AuthShell,
  Field,
  GoldButton,
  PasswordInput,
  authInputClass,
  errorText,
} from "@/components/auth/AuthShell";

const schema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirm: z.string().min(8, "Password must be at least 8 characters"),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Reset Password — CB16" },
      { name: "description", content: "Set a new password for your CB16 account." },
      { property: "og:title", content: "Reset Password — CB16" },
      { property: "og:description", content: "Set a new password for your CB16 account." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ResetPage,
});

function ResetPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<{ password?: string; confirm?: string; form?: string }>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ password, confirm });
    if (!parsed.success) {
      const fe = parsed.error.flatten().fieldErrors;
      setErrors({ password: fe.password?.[0], confirm: fe.confirm?.[0] });
      return;
    }
    setErrors({});
    setLoading(true);
    const { error } = await updatePassword(parsed.data.password);
    setLoading(false);
    if (error) {
      setErrors({ form: error });
      return;
    }
    setDone(true);
    setTimeout(() => navigate({ to: "/login" }), 2000);
  }

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="Redirecting to sign in…">
        <p className="text-center text-sm">
          <Link to="/login" className="text-[#C6A15B] hover:underline">
            Sign in now
          </Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset password" subtitle="Choose a new password">
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <Field label="New Password" htmlFor="password" error={errors.password}>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={authInputClass}
          />
        </Field>
        <Field label="Confirm Password" htmlFor="confirm" error={errors.confirm}>
          <PasswordInput
            id="confirm"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={authInputClass}
          />
        </Field>
        {errors.form && <p className={errorText}>{errors.form}</p>}
        <GoldButton type="submit" disabled={loading}>
          {loading ? "Updating…" : "Update Password"}
        </GoldButton>
      </form>
    </AuthShell>
  );
}
